import React, { useCallback, useEffect, useRef, useState } from "react";
import { Table, message } from "antd";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  ListCard,
  IconAction,
  RowActions,
  NameCell,
  DimCell,
  PHARMACY_PAGE_SIZE,
  listTableClass,
} from "../common/ListScreen";
import { useFittedRowsHeight } from "../common/useFittedRowsHeight";
import { getTablePagination } from "../../../utils/tablePagination";
import {
  getPharmacyConfigListApi,
  addPharmacyConfigApi,
} from "../../../services/pharmacyConfigs.services";
import { getErrorMessage } from "../../../helpers/error";
import SimpleConfigFormModal from "./SimpleConfigFormModal";
import VendorFormModal from "./VendorFormModal";
import { SIMPLE_CONFIG_TYPES } from "./configTypes";
import cfg from "./configurations.module.css";

/*
 * The six masters the module keeps, in the mockup's order.
 *
 * `label` names the list, `single` names one row of it - the difference
 * between "Companies" over the table and "Add Company" under it.
 */
const MASTERS = [
  { key: "vendors", label: "Vendors", single: "Vendor" },
  { key: "companies", label: "Companies", single: "Company" },
  { key: "categories", label: "Categories", single: "Category" },
  { key: "salts", label: "Salts", single: "Salt" },
  { key: "product-types", label: "Product Types", single: "Product Type" },
  { key: "item-types", label: "Item Types", single: "Item Type" },
];

/* What a vendor row says under its name - whatever of it there is. */
const vendorContact = (record) =>
  [record.mobile, record.email, record.state].filter(Boolean).join(" · ");

/*
 * Configurations, as the approved mockup lays it out.
 *
 * It was six Ant tabs, each with its own search, its own Add button and its own
 * table. Which masters were worth opening was the one thing that layout could
 * not say - so the tabs became a rail that carries each master's count, and the
 * card around it is the same card every other pharmacy list uses.
 */
const Configurations = () => {
  const [active, setActive] = useState("vendors");
  const master = MASTERS.find((m) => m.key === active) || MASTERS[0];
  const isVendors = active === "vendors";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [refresh, setRefresh] = useState(false);
  /* How many rows each master holds, for the rail. */
  const [counts, setCounts] = useState({});

  const [formOpen, setFormOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const refreshAll = () => setRefresh((prev) => !prev);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: PHARMACY_PAGE_SIZE });
    if (search) params.set("search", search);

    getPharmacyConfigListApi(active, params)
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [active, page, search, refresh]);

  /*
   * The rail's figures, read one master at a time.
   *
   * A single row is asked for from each: the count is on the envelope, so
   * nothing here pulls a page of records it will not show.
   */
  useEffect(() => {
    let cancelled = false;

    Promise.all(
      MASTERS.map((m) =>
        getPharmacyConfigListApi(m.key, new URLSearchParams({ page: 1, limit: 1 }))
          .then((res) => [m.key, res?.data?.count ?? null])
          .catch(() => [m.key, null])
      )
    ).then((pairs) => {
      if (!cancelled) setCounts(Object.fromEntries(pairs));
    });

    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const selectMaster = (key) => {
    setActive(key);
    setPage(1);
    setSearch("");
    setDraft(null);
  };

  /* ---- the row being typed ---------------------------------------------
   *
   * Vendors keep their dialog: a vendor is a name, a contact, a GSTIN and a
   * state, which is a form. Every other master is a name and perhaps a code,
   * which is a row.
   */
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const draftRef = useRef(null);

  useEffect(() => {
    if (draft) draftRef.current?.focus();
  }, [draft]);

  const hasShortCode = Boolean(
    SIMPLE_CONFIG_TYPES[active]?.fields.includes("short_code")
  );

  const saveDraft = useCallback(() => {
    const name = (draft?.name || "").trim();
    if (!name) {
      message.error(`Give the ${master.single.toLowerCase()} a name`);
      draftRef.current?.focus();
      return;
    }

    setSaving(true);
    addPharmacyConfigApi(active, {
      name,
      ...(hasShortCode && draft.short_code?.trim()
        ? { short_code: draft.short_code.trim() }
        : {}),
      is_active: true,
    })
      .then(() => {
        message.success(`${master.single} “${name}” added`);
        setDraft(null);
        refreshAll();
      })
      .catch((err) => message.error(getErrorMessage(err)))
      .finally(() => setSaving(false));
  }, [active, draft, hasShortCode, master.single]);

  const onDraftKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      saveDraft();
    } else if (event.key === "Escape") {
      event.preventDefault();
      setDraft(null);
    }
  };

  const openEdit = (record) => {
    setEditData(record);
    setFormOpen(true);
  };

  const buildColumns = () => {
    const action = {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 128,
      width: 90,
      render: (_, record) => (
        <RowActions>
          {/*
            * A master Curengo ships is read-only here: it is shared across
            * every organisation, so it is not this pharmacy's to change.
            */}
          <IconAction
            icon="edit"
            label={
              record.is_global
                ? "Provided by Curengo — it cannot be edited here"
                : `Edit ${master.single.toLowerCase()}`
            }
            disabled={record.is_global}
            onClick={() => openEdit(record)}
          />
        </RowActions>
      ),
    };

    if (isVendors) {
      return [
        {
          title: "Name",
          key: "name",
          render: (_, record) => <NameCell name={record.name} />,
        },
        {
          title: "Contact",
          key: "contact",
          render: (_, record) => <DimCell>{vendorContact(record) || "—"}</DimCell>,
        },
        {
          title: "GSTIN",
          dataIndex: "gstin",
          key: "gstin",
          render: (value) => <DimCell>{value || "—"}</DimCell>,
        },
        action,
      ];
    }

    return [
      {
        title: "Name",
        key: "name",
        render: (_, record) => <NameCell name={record.name} />,
      },
      ...(hasShortCode
        ? [
            {
              title: "Short Code",
              dataIndex: "short_code",
              key: "short_code",
              render: (value) => <DimCell>{value || "—"}</DimCell>,
            },
          ]
        : []),
      action,
    ];
  };

  const columns = buildColumns();

  const total = Number(data?.count) || 0;

  /* Re-fitted when the master changes, since Vendors has no add bar under the
     rows and the others do, and when the page changes, since the last page of
     a list is usually shorter than a full one. */
  const tableRef = useRef(null);
  const rowsHeight = useFittedRowsHeight(tableRef, [
    active,
    isVendors,
    page,
    total,
    /* The rows only reach their real height once the spinner clears. */
    loading,
    /* The row being typed is one more thing under the table to make room
       for, so opening and closing it re-fits. */
    Boolean(draft),
  ]);

  /* The draft row lines up with the table's own columns. */
  const draftColumns = hasShortCode
    ? "minmax(0, 2fr) minmax(0, 1fr) 110px"
    : "minmax(0, 1fr) 110px";

  return (
    <ListScreen>
      <ListHead
        title="Configurations"
        actions={
          isVendors ? (
            <UiButton
              onClick={() => {
                setEditData(null);
                setFormOpen(true);
              }}
            >
              <CgIcon name="plus" size={16} stroke={2.2} />
              Add Vendor
            </UiButton>
          ) : null
        }
      />

      <ListCard
        title={master.label}
        count={total}
        range={{ page, pageSize: PHARMACY_PAGE_SIZE, total }}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: `Search ${master.label.toLowerCase()}`,
        }}
        onClear={
          search
            ? () => {
                setSearch("");
                setPage(1);
              }
            : undefined
        }
      >
        <div className={cfg.wrap}>
          <nav className={cfg.rail}>
            {MASTERS.map((m) => (
              <button
                key={m.key}
                type="button"
                className={`${cfg.tab} ${active === m.key ? cfg.on : ""}`.trim()}
                aria-current={active === m.key}
                onClick={() => selectMaster(m.key)}
              >
                {m.label}
                <b>{counts[m.key] ?? "–"}</b>
              </button>
            ))}
          </nav>

          <div className={cfg.body} ref={tableRef}>
            <Table
              className={listTableClass}
              loading={loading}
              dataSource={data?.results || []}
              columns={columns}
              rowKey="id"
              /*
               * The rows scroll inside the table, not the page: the rail of
               * masters beside them is how you move between lists, and a rail
               * that scrolls away with a long list is a rail you have to
               * scroll back up to use.
               */
              scroll={{ x: "max-content", y: rowsHeight }}
              pagination={getTablePagination({
                current: page,
                pageSize: PHARMACY_PAGE_SIZE,
                total,
                onChange: (p) => setPage(p),
              })}
            />

            {draft ? (
              <div
                className={cfg.draft}
                style={{ gridTemplateColumns: draftColumns }}
              >
                <input
                  ref={draftRef}
                  className={cfg.draftIn}
                  value={draft.name}
                  placeholder="Name"
                  aria-label={`${master.single} name`}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  onKeyDown={onDraftKey}
                />
                {hasShortCode ? (
                  <input
                    className={cfg.draftIn}
                    value={draft.short_code}
                    placeholder="Short code (optional)"
                    aria-label="Short code"
                    onChange={(e) =>
                      setDraft({ ...draft, short_code: e.target.value })
                    }
                    onKeyDown={onDraftKey}
                  />
                ) : null}
                <div className={cfg.draftActs}>
                  <IconAction
                    icon="check"
                    label="Save (Enter)"
                    disabled={saving}
                    onClick={saveDraft}
                  />
                  <IconAction
                    icon="close"
                    label="Discard (Esc)"
                    disabled={saving}
                    onClick={() => setDraft(null)}
                  />
                </div>
              </div>
            ) : null}

            {isVendors ? null : (
              <div className={cfg.addBar}>
                <button
                  type="button"
                  className={cfg.add}
                  disabled={Boolean(draft) || Boolean(search)}
                  onClick={() => setDraft({ name: "", short_code: "" })}
                >
                  <CgIcon name="plus" size={15} stroke={2.4} />
                  Add {master.single}
                </button>
                <span className={cfg.addNote}>
                  {search
                    ? `Clear the search to add a ${master.single.toLowerCase()}`
                    : draft
                      ? "Enter saves the row · Esc discards it"
                      : ""}
                </span>
              </div>
            )}
          </div>
        </div>
      </ListCard>

      {formOpen && isVendors && (
        <VendorFormModal
          open={formOpen}
          setOpen={setFormOpen}
          editData={editData}
          refreshPage={refreshAll}
        />
      )}

      {formOpen && !isVendors && (
        <SimpleConfigFormModal
          resource={active}
          open={formOpen}
          setOpen={setFormOpen}
          editData={editData}
          refreshPage={refreshAll}
        />
      )}
    </ListScreen>
  );
};

export default Configurations;
